import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  alpha,
} from "@mui/material";
import {
  School as SchoolIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  PersonOutline as ReferrerIcon,
  KeyboardArrowDown as ChevronDownIcon,
  AccessTime as DurationIcon,
  Login as LastLoginIcon,
  EventAvailable as RegisteredIcon,
  Call as CallIcon,
  WhatsApp as WhatsAppIcon,
  InfoOutlined as DetailsIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

// Created By Cursor — digits for tel: / wa.me
const normalizeDialPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  return digits;
};

const emptyForm = {
  school_name: "",
  admin_name: "",
  admin_phone: "",
  school_address: "",
  country: "",
  state: "",
};

const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PAGE_SIZE = 100;

// Created By Cursor — mirror MdlSelectMultipleSchool fetch helpers
const Schools = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const searchInputRef = useRef(null);

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [financialYears, setFinancialYears] = useState([]);
  const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null);
  const [financialYearAnchorEl, setFinancialYearAnchorEl] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // Same baseYear logic as Select School dialog (Created By Cursor)
  const baseYear = useMemo(() => {
    if (!selectedFinancialYearId) return new Date().getFullYear();

    const fy = financialYears.find((item) => {
      const id = item.financial_year_id || item.year_id || item.id || item.year;
      return String(id) === String(selectedFinancialYearId);
    });

    if (fy) {
      if (fy.start_date) {
        const y = Number(String(fy.start_date).slice(0, 4));
        if (!isNaN(y)) return y;
      }
      const year = fy.year || fy.start_year || fy.calendar_year;
      if (year && !isNaN(Number(year))) return Number(year);
      if (fy.financial_year_name) {
        const match = String(fy.financial_year_name).match(/\d{4}/);
        if (match) return Number(match[0]);
      }
    }

    const numericId = Number(selectedFinancialYearId);
    return !isNaN(numericId) ? numericId : new Date().getFullYear();
  }, [selectedFinancialYearId, financialYears]);

  const financialYearMonths = useMemo(() => {
    const months = [];
    for (let i = 4; i <= 12; i++) months.push({ monthNumber: i, year: baseYear });
    for (let i = 1; i <= 3; i++) months.push({ monthNumber: i, year: baseYear + 1 });
    return months;
  }, [baseYear]);

  // Created By Cursor — current calendar month within selected FY (quick pick)
  const currentMonthEntry = useMemo(() => {
    const now = new Date();
    const monthNumber = now.getMonth() + 1;
    const year = now.getFullYear();
    return (
      financialYearMonths.find((m) => m.monthNumber === monthNumber && m.year === year) || null
    );
  }, [financialYearMonths]);

  const selectedFyLabel = useMemo(() => {
    if (loadingFinancialYears) return "Loading…";
    if (!selectedFinancialYearId) return "Select";
    const fy = financialYears.find((item) => {
      const id = item.financial_year_id || item.year_id || item.id || item.year;
      return String(id) === String(selectedFinancialYearId);
    });
    if (!fy) return String(selectedFinancialYearId);
    return (
      fy.financial_year_name ||
      fy.name ||
      fy.label ||
      String(fy.financial_year_id || fy.year_id || fy.id || fy.year || selectedFinancialYearId)
    );
  }, [financialYears, selectedFinancialYearId, loadingFinancialYears]);

  // Mirror Select School: getFinancialYearList (Created By Cursor)
  const loadFinancialYears = useCallback(async () => {
    setLoadingFinancialYears(true);
    try {
      const result = await apiService.getFinancialYearList();
      if (result.success) {
        const list = result.data || [];
        setFinancialYears(list);

        if (list.length > 0) {
          const currentRow =
            list.find((fy) => {
              const status =
                fy.status ||
                fy.financial_year_status ||
                fy.current_status ||
                fy.is_current ||
                fy.isCurrent ||
                "";
              return typeof status === "string" && status === "Current";
            }) || list[0];

          const id =
            currentRow.financial_year_id ||
            currentRow.year_id ||
            currentRow.id ||
            currentRow.year ||
            new Date().getFullYear();
          setSelectedFinancialYearId((prev) => prev || id);
        }
      } else {
        toast.error(result.error || "Failed to load financial years");
      }
    } catch (error) {
      toast.error("Error loading financial years: " + error.message);
    } finally {
      setLoadingFinancialYears(false);
    }
  }, []);

  // Mirror Select School: getMySchools(fy, YYYY-MM) — 100 per page (Created By Cursor)
  const fetchSchools = useCallback(async () => {
    if (!selectedFinancialYearId || !selectedMonth) {
      setSchools([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const formattedMonth = `${selectedMonth >= 4 ? baseYear : baseYear + 1}-${String(
        selectedMonth
      ).padStart(2, "0")}`;
      const offset = (page - 1) * PAGE_SIZE;

      const result = await apiService.getMySchools(
        selectedFinancialYearId,
        formattedMonth,
        PAGE_SIZE,
        offset,
        "DESC",
        statusFilter === "all" ? "all" : statusFilter
      );

      if (result.success) {
        const raw = result.data;
        const rows = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.rows)
            ? raw.rows
            : [];
        setSchools(
          rows.map((s) => ({
            ...s,
            school_code: s.school_code || s.schoolCode || s.school_id || s.id || "",
            school_name: s.school_name || s.name || "",
            admin_name: s.admin_name || s.contactPerson || "",
            admin_phone: s.admin_phone || s.admin_number || s.phone || "",
            school_address: s.school_address || s.address || "",
            total_student: s.total_student ?? s.total_students ?? 0,
            // Created By Cursor — product + subscription status (Grace / Expired like CRM dashboard)
            status:
              s.status ||
              s.product_status_name ||
              (String(s.product_status) === "1"
                ? "Demo"
                : String(s.product_status) === "2"
                  ? "Active"
                  : String(s.product_status) === "3"
                    ? "InActive"
                    : "Demo Not Start"),
            billing_status: s.billing_status || null,
            subscription_status: s.subscription_status || null,
            service_end_date: s.service_end_date || null,
            grace_end_date: s.grace_end_date || null,
            grace_days_left:
              s.grace_days_left != null && s.grace_days_left !== ""
                ? Number(s.grace_days_left)
                : null,
            product_status: s.product_status,
            product_status_name: s.product_status_name || null,
            referal_code: s.referal_code || null,
            referred_by: s.referred_by || null,
            referred_by_type: s.referred_by_type || null,
            referred_by_school_code: s.referred_by_school_code || null,
            date_added: s.date_added || s.dateAdded || null,
            duration: s.duration || null,
            last_login: s.last_login || s.lastLogin || null,
            demo_expired: Boolean(s.demo_expired),
            demo_days_left:
              s.demo_days_left != null && s.demo_days_left !== ""
                ? Number(s.demo_days_left)
                : null,
            demo_label: s.demo_label || null,
            demo_days_allowed: s.demo_days_allowed != null ? Number(s.demo_days_allowed) : null,
            demo_days_elapsed: s.demo_days_elapsed != null ? Number(s.demo_days_elapsed) : null,
          }))
        );
        setTotalCount(
          typeof result.total === "number"
            ? result.total
            : rows.length > 0 && rows[0].total != null
              ? Number(rows[0].total)
              : rows.length
        );
      } else {
        toast.error(result.error || "Failed to load schools");
        setSchools([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Error loading schools: " + error.message);
      setSchools([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedFinancialYearId, selectedMonth, baseYear, page, statusFilter]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  useEffect(() => {
    setSelectedMonth(null);
    setPage(1);
  }, [selectedFinancialYearId]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  //Created By Cursor — dashboard deep-links: ?add=1 | ?focus=search
  useEffect(() => {
    const add = searchParams.get("add");
    const focus = searchParams.get("focus");
    if (add === "1" || add === "true") {
      setAddDialogOpen(true);
      setSearchParams({}, { replace: true });
      return;
    }
    if (focus === "search") {
      setSearchParams({}, { replace: true });
      const t = window.setTimeout(() => {
        searchInputRef.current?.focus?.();
      }, 120);
      return () => window.clearTimeout(t);
    }
  }, [searchParams, setSearchParams]);

  // Status is filtered server-side; search stays client-side on current page
  const filteredSchools = schools.filter((school) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    const name = (school.school_name || "").toLowerCase();
    const code = (school.school_code || "").toLowerCase();
    const contact = (school.admin_name || "").toLowerCase();
    const phone = String(school.admin_phone || "").toLowerCase();
    const location = `${school.state || ""} ${school.country || ""}`.toLowerCase();
    return (
      name.includes(term) ||
      code.includes(term) ||
      contact.includes(term) ||
      phone.includes(term) ||
      location.includes(term)
    );
  });

  const displaySchoolCount = searchTerm.trim()
    ? filteredSchools.length
    : totalCount;

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleFilterClick = (event) => setFilterAnchor(event.currentTarget);
  const handleFilterClose = () => setFilterAnchor(null);
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
    handleFilterClose();
  };

  const handleFinancialYearSelect = (year) => {
    setSelectedFinancialYearId(year);
    setPage(1);
    setFinancialYearAnchorEl(null);
  };

  const handleMonthSelect = (monthNumber) => {
    setSelectedMonth((prev) => (prev === monthNumber ? null : monthNumber));
    setPage(1);
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddSubmit = async () => {
    if (!addForm.school_name || !addForm.admin_name || !addForm.admin_phone) {
      toast.error("School name, contact person and phone are required");
      return;
    }
    setSaving(true);
    const result = await apiService.createSchool(addForm);
    setSaving(false);

    if (result.success) {
      toast.success("School added successfully");
      setAddDialogOpen(false);
      setAddForm(emptyForm);
      fetchSchools();
    } else {
      toast.error(result.error || "Failed to add school");
    }
  };

  const openEditDialog = (school) => {
    setEditForm({
      school_code: school.school_code,
      school_name: school.school_name || "",
      admin_name: school.admin_name || "",
      admin_phone: school.admin_phone || "",
      school_address: school.school_address || "",
      status: school.status || "Active",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editForm.school_name || !editForm.admin_name || !editForm.admin_phone) {
      toast.error("School name, contact person and phone are required");
      return;
    }
    setSaving(true);
    const result = await apiService.updateSchool(editForm);
    setSaving(false);

    if (result.success) {
      toast.success("School updated successfully");
      setEditDialogOpen(false);
      setEditForm(null);
      fetchSchools();
    } else {
      toast.error(result.error || "Failed to update school");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await apiService.deleteSchool(deleteTarget.school_code);
    if (result.success) {
      toast.success("School deleted");
      fetchSchools();
    } else {
      toast.error(result.error || "Failed to delete school");
    }
    setDeleteTarget(null);
  };

  // Created By Cursor — Demo / Active / InActive / Demo Not Start / Grace / Expired
  const getStatusColor = (status, school) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") return "success";
    //Created By Cursor — Grace uses red (error) like urgency, not warning amber
    if (s === "grace" || s === "expired") return "error";
    if (s === "demo") return school?.demo_expired ? "warning" : "info";
    if (s === "demo not start") return "warning";
    if (s === "inactive") return "default";
    return "default";
  };
  const getStatusIcon = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") return <ActiveIcon />;
    if (s === "grace" || s === "expired") return <CalendarIcon />;
    if (s === "demo" || s === "demo not start") return <CalendarIcon />;
    return <InactiveIcon />;
  };
  const getGraceDaysLeft = (school) => {
    if (school.grace_days_left != null && !Number.isNaN(Number(school.grace_days_left))) {
      return Math.max(0, Number(school.grace_days_left));
    }
    if (!school.grace_end_date) return null;
    const end = new Date(school.grace_end_date).getTime();
    if (Number.isNaN(end)) return null;
    return Math.max(0, Math.floor((end - Date.now()) / 86400000));
  };
  const getStatusLabel = (school) => {
    const s = String(school.status || "").toLowerCase();
    if (s === "grace") {
      const left = getGraceDaysLeft(school);
      if (left == null) return "Grace";
      if (left <= 0) return "Grace · Last day";
      return `Grace · ${left} day${left === 1 ? "" : "s"} left`;
    }
    if (s === "expired") return "Service Expired";
    if (s === "demo" && school.demo_label) {
      return school.demo_expired ? "Demo expired" : `Demo · ${school.demo_label}`;
    }
    return school.status || "Demo Not Start";
  };

  const shellSx = {
    borderRadius: 2.5,
    border: `1px solid ${crmColors.border.default}`,
    boxShadow: "0 6px 18px -14px rgba(15, 31, 47, 0.22)",
    overflow: "hidden",
    background: crmColors.background.paper,
  };

  return (
    <PageContentLayout
      title="All Schools"
      subtitle="Browse schools by financial year and month"
      darkBar
      breadcrumbs={[
        { label: "All Schools", icon: <SchoolIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 800,
            px: 1.75,
            background: crmColors.energy.main,
            backgroundImage: "none",
            color: "#fff",
            border: `1px solid ${alpha("#fff", 0.55)}`,
            "&:hover": {
              background: crmColors.energy.dark,
              backgroundImage: "none",
            },
          }}
        >
          Add School
        </Button>
      }
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {/* Filter / period panel */}
        <Card sx={{ ...shellSx, overflow: "visible" }}>
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, "&:last-child": { pb: { xs: 1.5, md: 2 } } }}>
            <Stack spacing={1.75}>
              {/* Search + Year + Status */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.25,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  inputRef={searchInputRef}
                  placeholder="Search by school, code, contact, location…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: crmColors.primary.main, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: "1 1 220px",
                    minWidth: 0,
                    "& .MuiOutlinedInput-root": {
                      height: 42,
                      borderRadius: "12px",
                      bgcolor: "#fff",
                      boxShadow: `inset 0 0 0 1px ${crmColors.border.default}`,
                      transition: "box-shadow 0.15s ease",
                      "& fieldset": { border: "none" },
                      "&:hover": {
                        boxShadow: `inset 0 0 0 1px ${crmColors.border.primaryTint}`,
                      },
                      "&.Mui-focused": {
                        boxShadow: `inset 0 0 0 2px ${crmColors.primary.main}`,
                      },
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  disableElevation
                  endIcon={<ChevronDownIcon sx={{ fontSize: 18 }} />}
                  startIcon={
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(crmColors.primary.main, 0.12),
                        color: crmColors.primary.main,
                      }}
                    >
                      <CalendarIcon sx={{ fontSize: 15 }} />
                    </Box>
                  }
                  onClick={(e) => setFinancialYearAnchorEl(e.currentTarget)}
                  disabled={loadingFinancialYears}
                  sx={{
                    textTransform: "none",
                    height: 42,
                    px: 1.25,
                    borderRadius: "12px",
                    fontWeight: 700,
                    fontSize: 13,
                    borderColor: crmColors.border.primaryTint,
                    color: crmColors.text.primary,
                    bgcolor: alpha(crmColors.primary.main, 0.04),
                    "& .MuiButton-startIcon": { mr: 1 },
                    "&:hover": {
                      borderColor: crmColors.primary.main,
                      bgcolor: alpha(crmColors.primary.main, 0.08),
                    },
                  }}
                >
                  <Box sx={{ textAlign: "left", lineHeight: 1.15 }}>
                    <Typography
                      component="span"
                      sx={{
                        display: "block",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: crmColors.text.secondary,
                      }}
                    >
                      Year
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 800 }}>
                      {selectedFyLabel}
                    </Typography>
                  </Box>
                </Button>
                <Menu
                  anchorEl={financialYearAnchorEl}
                  open={Boolean(financialYearAnchorEl)}
                  onClose={() => setFinancialYearAnchorEl(null)}
                  PaperProps={{
                    sx: {
                      borderRadius: 2.5,
                      mt: 1,
                      minWidth: 220,
                      border: `1px solid ${crmColors.border.default}`,
                      boxShadow: "0 12px 28px -12px rgba(15,31,47,0.28)",
                    },
                  }}
                >
                  {loadingFinancialYears && <MenuItem disabled>Loading...</MenuItem>}
                  {!loadingFinancialYears &&
                    financialYears.map((fy, index) => {
                      const id = fy.financial_year_id || fy.year_id || fy.id || fy.year;
                      const label = fy.financial_year_name || fy.name || fy.label || String(id || "");
                      const isCurrent = String(fy.status || "").toLowerCase() === "current";
                      if (!id && !label) return null;
                      const selected = String(id) === String(selectedFinancialYearId);
                      return (
                        <MenuItem
                          key={id || index}
                          onClick={() => handleFinancialYearSelect(id)}
                          selected={selected}
                          sx={{
                            borderRadius: 1.5,
                            mx: 0.75,
                            my: 0.25,
                            fontWeight: selected ? 800 : 600,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                            <Box sx={{ flex: 1 }}>{label}</Box>
                            {isCurrent && (
                              <Chip
                                size="small"
                                label="Current"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  bgcolor: alpha(crmColors.primary.main, 0.12),
                                  color: crmColors.primary.main,
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                </Menu>

                <Button
                  variant={statusFilter !== "all" ? "contained" : "outlined"}
                  size="small"
                  disableElevation
                  startIcon={<FilterIcon sx={{ fontSize: 16 }} />}
                  endIcon={<ChevronDownIcon sx={{ fontSize: 18 }} />}
                  onClick={handleFilterClick}
                  sx={{
                    textTransform: "none",
                    height: 42,
                    px: 1.5,
                    borderRadius: "12px",
                    fontWeight: 700,
                    fontSize: 13,
                    backgroundImage: "none",
                    ...(statusFilter === "all"
                      ? {
                          borderColor: crmColors.border.default,
                          color: crmColors.text.secondary,
                          bgcolor: "#fff",
                        }
                      : {
                          background: crmColors.primary.main,
                          color: "#fff",
                        }),
                  }}
                >
                  {statusFilter === "all" ? "All status" : statusFilter}
                </Button>
                <Menu
                  anchorEl={filterAnchor}
                  open={Boolean(filterAnchor)}
                  onClose={handleFilterClose}
                  PaperProps={{ sx: { borderRadius: 2.5, mt: 1, minWidth: 160 } }}
                >
                  <MenuItem onClick={() => handleStatusFilter("all")}>All status</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Demo")}>Demo</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Demo Not Start")}>Demo Not Start</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Active")}>Active</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Grace")}>Grace</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Expired")}>Service Expired</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("InActive")}>InActive</MenuItem>
                </Menu>

                {selectedMonth && (
                  <Chip
                    size="small"
                    label={`${displaySchoolCount} schools`}
                    sx={{
                      height: 32,
                      px: 0.5,
                      fontWeight: 800,
                      fontSize: 12,
                      borderRadius: "10px",
                      bgcolor: alpha(crmColors.primary.main, 0.1),
                      color: crmColors.primary.main,
                      border: `1px solid ${crmColors.border.primarySubtle}`,
                    }}
                  />
                )}
              </Box>

              {/* Month rail */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: crmColors.text.secondary,
                    }}
                  >
                    Month
                  </Typography>
                  {selectedMonth && (
                    <Typography variant="caption" sx={{ color: crmColors.text.secondary, fontWeight: 600 }}>
                      {MONTH_FULL[selectedMonth - 1]} ·{" "}
                      {selectedMonth >= 4 ? baseYear : baseYear + 1}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "stretch",
                    gap: 1,
                    p: 1,
                    borderRadius: "14px",
                    background: `linear-gradient(180deg, ${crmColors.primary.surfaceMuted} 0%, ${alpha(
                      crmColors.primary.accent,
                      0.06
                    )} 100%)`,
                    border: `1px solid ${crmColors.border.primarySubtle}`,
                  }}
                >
                  {currentMonthEntry && (
                    <>
                      <Box
                        onClick={() => handleMonthSelect(currentMonthEntry.monthNumber)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleMonthSelect(currentMonthEntry.monthNumber);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          flex: "0 0 auto",
                          minWidth: 92,
                          borderRadius: "12px",
                          minHeight: 52,
                          px: 1.25,
                          py: 0.75,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.2,
                          border:
                            selectedMonth === currentMonthEntry.monthNumber
                              ? `2px solid ${crmColors.energy.dark}`
                              : `1.5px solid ${alpha(crmColors.energy.main, 0.55)}`,
                          background:
                            selectedMonth === currentMonthEntry.monthNumber
                              ? crmColors.energy.main
                              : alpha(crmColors.energy.main, 0.1),
                          boxShadow:
                            selectedMonth === currentMonthEntry.monthNumber
                              ? `0 6px 14px ${alpha(crmColors.energy.main, 0.35)}`
                              : "none",
                          transition: "transform 0.14s ease, background 0.14s ease",
                          "&:hover": { transform: "translateY(-1px)" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 9,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color:
                              selectedMonth === currentMonthEntry.monthNumber
                                ? alpha("#fff", 0.9)
                                : crmColors.energy.dark,
                            lineHeight: 1,
                          }}
                        >
                          Current
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 12,
                            lineHeight: 1.15,
                            textAlign: "center",
                            color:
                              selectedMonth === currentMonthEntry.monthNumber
                                ? "#fff"
                                : crmColors.text.primary,
                          }}
                        >
                          {MONTH_FULL[currentMonthEntry.monthNumber - 1]}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 10,
                            color:
                              selectedMonth === currentMonthEntry.monthNumber
                                ? alpha("#fff", 0.88)
                                : crmColors.text.secondary,
                          }}
                        >
                          {currentMonthEntry.year}
                        </Typography>
                      </Box>

                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          mx: 0.35,
                          borderColor: alpha(crmColors.primary.main, 0.2),
                          borderRightWidth: 2,
                          borderRadius: 1,
                        }}
                      />
                    </>
                  )}

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(3, 1fr)",
                        sm: "repeat(4, 1fr)",
                        md: "repeat(6, 1fr)",
                        lg: "repeat(12, 1fr)",
                      },
                      gap: 0.75,
                    }}
                  >
                    {financialYearMonths.map(({ monthNumber, year }) => {
                      const isSelected = selectedMonth === monthNumber;
                      return (
                        <Box
                          key={`${monthNumber}-${year}`}
                          onClick={() => handleMonthSelect(monthNumber)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") handleMonthSelect(monthNumber);
                          }}
                          sx={{
                            cursor: "pointer",
                            borderRadius: "12px",
                            minHeight: 52,
                            px: 0.5,
                            py: 0.75,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.2,
                            border: isSelected
                              ? `2px solid ${crmColors.primary.dark}`
                              : `1px solid ${alpha("#fff", 0.9)}`,
                            background: isSelected ? crmColors.primary.main : "#fff",
                            boxShadow: isSelected
                              ? `0 6px 14px ${alpha(crmColors.primary.main, 0.32)}`
                              : `0 1px 3px ${alpha("#0f1f2f", 0.05)}`,
                            transition:
                              "transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              borderColor: crmColors.primary.main,
                              boxShadow: isSelected
                                ? `0 8px 16px ${alpha(crmColors.primary.main, 0.38)}`
                                : `0 4px 10px ${alpha(crmColors.primary.main, 0.12)}`,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: 11, md: 11.5 },
                              lineHeight: 1.15,
                              textAlign: "center",
                              color: isSelected ? "#fff" : crmColors.text.primary,
                              px: 0.25,
                            }}
                          >
                            {MONTH_FULL[monthNumber - 1]}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: isSelected ? 700 : 600,
                              fontSize: 10,
                              lineHeight: 1.1,
                              color: isSelected ? alpha("#fff", 0.88) : crmColors.text.secondary,
                            }}
                          >
                            {year}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Results */}
        <Card sx={shellSx}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 8 }}>
                <CircularProgress size={34} />
                <Typography variant="body2" sx={{ color: crmColors.text.secondary }}>
                  Loading schools…
                </Typography>
              </Box>
            ) : !selectedMonth ? (
              <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "20px",
                    display: "grid",
                    placeItems: "center",
                    background: `linear-gradient(145deg, ${crmColors.primary.surfaceMuted}, ${alpha(
                      crmColors.primary.accent,
                      0.2
                    )})`,
                    color: crmColors.primary.main,
                  }}
                >
                  <CalendarIcon />
                </Box>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Pick a month to continue</Typography>
                <Typography variant="body2" sx={{ color: crmColors.text.secondary, maxWidth: 360, mx: "auto" }}>
                  Choose financial year and a month above — schools load with the same API as the Select School dialog.
                </Typography>
              </Box>
            ) : filteredSchools.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                <SchoolIcon sx={{ fontSize: 42, color: crmColors.border.hover, mb: 1 }} />
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No schools found</Typography>
                <Typography variant="body2" sx={{ color: crmColors.text.secondary }}>
                  Try another month, clear search, or change status filter.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.75}>
                  {filteredSchools.map((school, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={school.school_code || `${school.school_id}-${index}`}
                    >
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: 2.5,
                          border: `1px solid ${crmColors.border.default}`,
                          boxShadow: "none",
                          background: "#fff",
                          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": {
                            borderColor: crmColors.border.primaryTint,
                            boxShadow: `0 8px 20px ${alpha(crmColors.primary.main, 0.1)}`,
                          },
                          "&::before": { display: "none" },
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, overflow: "visible" }}>
                          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.5 }}>
                            <Avatar
                              src={school.school_logo_path || undefined}
                              sx={{
                                width: 44,
                                height: 44,
                                fontSize: 16,
                                fontWeight: 800,
                                bgcolor: crmColors.primary.main,
                                flexShrink: 0,
                              }}
                            >
                              {(school.school_name || "S").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: 14,
                                  lineHeight: 1.3,
                                  mb: 0.35,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {school.school_name}
                                {school.school_code ? (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      color: crmColors.text.secondary,
                                      ml: 0.5,
                                    }}
                                  >
                                    ({school.school_code})
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: crmColors.text.secondary,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.35,
                                }}
                              >
                                {school.school_address || "—"}
                              </Typography>
                            </Box>
                            <Chip
                              icon={getStatusIcon(school.status)}
                              label={getStatusLabel(school)}
                              color={getStatusColor(school.status, school)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 1.5,
                                height: 24,
                                maxWidth: 220,
                                flexShrink: 0,
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  px: 0.75,
                                },
                              }}
                            />
                          </Box>

                          <Stack spacing={0.75} sx={{ mb: 0 }}>
                            {/* Created By Cursor — name | number + call + WhatsApp */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontWeight: 700,
                                  color: crmColors.text.primary,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                    color: crmColors.text.secondary,
                                  }}
                                >
                                  Admin:
                                </Typography>{" "}
                                {school.admin_name || "—"}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: crmColors.text.secondary,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {school.admin_phone || "—"}
                                </Typography>
                                {normalizeDialPhone(school.admin_phone) ? (
                                  <>
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={`tel:+${normalizeDialPhone(school.admin_phone)}`}
                                      title="Call"
                                      aria-label="Call admin"
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: alpha(crmColors.primary.main, 0.1),
                                        color: crmColors.primary.main,
                                        "&:hover": {
                                          bgcolor: alpha(crmColors.primary.main, 0.18),
                                        },
                                      }}
                                    >
                                      <CallIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={`https://wa.me/${normalizeDialPhone(school.admin_phone)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="WhatsApp"
                                      aria-label="WhatsApp admin"
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        bgcolor: alpha("#25D366", 0.12),
                                        color: "#128C7E",
                                        "&:hover": { bgcolor: alpha("#25D366", 0.22) },
                                      }}
                                    >
                                      <WhatsAppIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </>
                                ) : null}
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: crmColors.text.secondary,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <LocationIcon sx={{ fontSize: 14, color: crmColors.primary.main }} />
                              {[school.state, school.country].filter(Boolean).join(", ") || "—"}
                            </Typography>
                            {/* Created By Cursor — referral directly below state */}
                            {(school.referred_by || school.referal_code) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: crmColors.primary.main,
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                  fontWeight: 700,
                                  lineHeight: 1.35,
                                  mt: 0.25,
                                }}
                              >
                                <ReferrerIcon sx={{ fontSize: 14, mt: "1px", flexShrink: 0 }} />
                                <Box component="span" sx={{ minWidth: 0, wordBreak: "break-word" }}>
                                  {school.referred_by
                                    ? `Referred by ${school.referred_by}${
                                        school.referred_by_school_code
                                          ? ` (${school.referred_by_school_code})`
                                          : ""
                                      }`
                                    : `Referral: ${school.referal_code}`}
                                </Box>
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Chip
                                size="small"
                                icon={<PeopleIcon sx={{ fontSize: "14px !important" }} />}
                                label={`${Number(school.total_student || 0).toLocaleString()} students`}
                                sx={{
                                  height: 26,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  borderRadius: "8px",
                                  bgcolor: alpha(crmColors.primary.main, 0.1),
                                  color: crmColors.primary.main,
                                  "& .MuiChip-icon": { color: crmColors.primary.main },
                                }}
                              />
                              <Chip
                                size="small"
                                icon={<RegisteredIcon sx={{ fontSize: "14px !important" }} />}
                                label={
                                  school.date_added
                                    ? new Date(school.date_added).toLocaleDateString()
                                    : "No date"
                                }
                                sx={{
                                  height: 26,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  borderRadius: "8px",
                                  bgcolor: alpha(crmColors.hero.start, 0.1),
                                  color: crmColors.hero.start,
                                  "& .MuiChip-icon": { color: crmColors.hero.start },
                                }}
                              />
                              <Chip
                                size="small"
                                icon={<DurationIcon sx={{ fontSize: "14px !important" }} />}
                                label={school.duration || "—"}
                                sx={{
                                  height: 26,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  borderRadius: "8px",
                                  bgcolor: alpha(crmColors.energy.main, 0.12),
                                  color: crmColors.energy.dark,
                                  "& .MuiChip-icon": { color: crmColors.energy.main },
                                }}
                              />
                              <Chip
                                size="small"
                                icon={<LastLoginIcon sx={{ fontSize: "14px !important" }} />}
                                label={
                                  school.last_login
                                    ? new Date(school.last_login).toLocaleString()
                                    : "Never"
                                }
                                sx={{
                                  height: 26,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  borderRadius: "8px",
                                  bgcolor: alpha(crmColors.text.secondary, 0.1),
                                  color: crmColors.text.secondary,
                                  "& .MuiChip-icon": { color: crmColors.text.secondary },
                                }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<DetailsIcon sx={{ fontSize: 15 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (school.school_code) {
                                    navigate(`/schools/detail/${encodeURIComponent(school.school_code)}`);
                                  }
                                }}
                                disabled={!school.school_code}
                                sx={{
                                  textTransform: "none",
                                  height: 26,
                                  minHeight: 26,
                                  px: 1.25,
                                  borderRadius: "8px",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  background: crmColors.primary.main,
                                  backgroundImage: "none",
                                  color: "#fff",
                                  "&:hover": {
                                    background: crmColors.primary.dark,
                                    backgroundImage: "none",
                                  },
                                }}
                              >
                                Details
                              </Button>
                            </Box>
                          </Stack>

                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {totalCount > PAGE_SIZE && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      mt: 2.5,
                      pt: 2,
                      borderTop: `1px solid ${crmColors.border.default}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: crmColors.text.secondary, fontWeight: 600 }}>
                      Showing {(page - 1) * PAGE_SIZE + 1}–
                      {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      siblingCount={1}
                      boundaryCount={1}
                    />
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Add School Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Add New School</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Name"
                required
                value={addForm.school_name}
                onChange={(e) => setAddForm({ ...addForm, school_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                required
                value={addForm.admin_name}
                onChange={(e) => setAddForm({ ...addForm, admin_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                required
                value={addForm.admin_phone}
                onChange={(e) => setAddForm({ ...addForm, admin_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={addForm.country}
                onChange={(e) => setAddForm({ ...addForm, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={addForm.state}
                onChange={(e) => setAddForm({ ...addForm, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={addForm.school_address}
                onChange={(e) => setAddForm({ ...addForm, school_address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={saving} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Adding..." : "Add School"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit School Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Edit School</DialogTitle>
        <DialogContent>
          {editForm && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School Name"
                  required
                  value={editForm.school_name}
                  onChange={(e) => setEditForm({ ...editForm, school_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  required
                  value={editForm.admin_name}
                  onChange={(e) => setEditForm({ ...editForm, admin_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  required
                  value={editForm.admin_phone}
                  onChange={(e) => setEditForm({ ...editForm, admin_phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <MenuItem value="Demo">Demo</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="InActive">InActive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={editForm.school_address}
                  onChange={(e) => setEditForm({ ...editForm, school_address: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={saving} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete School</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteTarget?.school_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ textTransform: "none", fontWeight: 700 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default Schools;
